import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { QrCode, Zap, Shield, Download, Smartphone, Palette, Globe, Sparkles, TrendingUp, Lock, BarChart3, Layers, ArrowRight, ArrowUpRight, Scan, Activity, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/layout/Layout';
import { useRef, useState } from 'react';

const features = [
  {
    icon: Zap,
    title: 'Instant Generation',
    description: 'Create QR codes in milliseconds with real-time preview and instant downloads.',
    number: '01',
  },
  {
    icon: Palette,
    title: 'Full Customization',
    description: '12+ styles, custom gradients, logos, and frames — make it truly yours.',
    number: '02',
  },
  {
    icon: Globe,
    title: '25+ Content Types',
    description: 'URLs, vCards, WiFi, social media, crypto, AR, and more.',
    number: '03',
  },
  {
    icon: TrendingUp,
    title: 'Real-time Analytics',
    description: 'Track scans, locations, devices, and conversion rates as they happen.',
    number: '04',
  },
  {
    icon: Lock,
    title: 'Enterprise Security',
    description: 'Password protection, expiration dates, and domain whitelisting.',
    number: '05',
  },
  {
    icon: Layers,
    title: 'Dynamic QR Codes',
    description: 'Update content anytime without reprinting — powered by cloud.',
    number: '06',
  },
];

const stats = [
  { value: '10M+', label: 'QR codes created' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '50K+', label: 'Active users' },
  { value: '4.9/5', label: 'User rating' },
];

function Feature3DCard({ feature, index }: { feature: typeof features[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 20 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="perspective-1200"
    >
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY }}
        className="preserve-3d group bg-card border border-border/50 rounded-2xl p-8 md:p-10 relative overflow-hidden cursor-default transition-shadow duration-300 hover:shadow-xl hover:shadow-primary/5"
      >
        {/* Shimmer overlay on hover */}
        <div className={`absolute inset-0 shimmer-3d pointer-events-none transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
        
        {/* Glow spot that follows mouse */}
        <motion.div
          className="absolute w-40 h-40 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 70%)',
            x: useTransform(mouseX, [-0.5, 0.5], [-40, 200]),
            y: useTransform(mouseY, [-0.5, 0.5], [-40, 200]),
          }}
        />

        {/* Number */}
        <span className="text-[11px] font-bold text-muted-foreground/40 tracking-widest absolute top-6 right-6 tabular-nums">
          {feature.number}
        </span>
        
        {/* 3D Icon */}
        <motion.div 
          className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 relative"
          style={{
            translateZ: isHovered ? 30 : 0,
          }}
          animate={{
            scale: isHovered ? 1.1 : 1,
            boxShadow: isHovered ? '0 8px 24px hsl(var(--primary) / 0.15)' : '0 0px 0px transparent',
          }}
          transition={{ duration: 0.3 }}
        >
          <feature.icon className="h-5 w-5 text-primary" />
        </motion.div>
        
        {/* Content */}
        <h3 className="text-lg font-bold mb-2 tracking-tight relative z-10">
          {feature.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed relative z-10">
          {feature.description}
        </p>
      </motion.div>
    </motion.div>
  );
}

export default function Index() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Dot grid background */}
        <div className="absolute inset-0 dot-grid" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
        
        {/* Ambient glow */}
        <div className="absolute top-[10%] right-[15%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[20%] left-[10%] w-[300px] h-[300px] rounded-full bg-accent/5 blur-[100px] pointer-events-none" />

        <div className="max-w-[1400px] mx-auto px-6 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            {/* Left: Text content */}
            <div>
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/25 bg-primary/5 mb-8"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-semibold text-primary tracking-wide uppercase">
                  AI-Powered QR Platform
                </span>
              </motion.div>

              {/* Main headline */}
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="text-5xl sm:text-6xl md:text-7xl font-black leading-[0.95] tracking-tight mb-8"
              >
                QR codes
                <br />
                that actually{' '}
                <span className="gradient-text">work</span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-lg md:text-xl text-muted-foreground mb-10 max-w-xl leading-relaxed"
              >
                Dynamic content, real-time analytics, AI design suggestions, and enterprise-grade security. All in one platform.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <Link to="/create">
                  <Button size="lg" className="group h-12 px-7 text-[15px] font-semibold gradient-hero text-white shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all rounded-xl">
                    Start Creating Free
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="h-12 px-7 text-[15px] font-semibold border-border/60 hover:bg-muted/50 rounded-xl">
                    View Pricing
                  </Button>
                </Link>
              </motion.div>

              {/* Stats row */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="flex flex-wrap gap-8 md:gap-12 mt-16 pt-8 border-t border-border/40"
              >
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.6 + index * 0.08 }}
                  >
                    <div className="text-2xl md:text-3xl font-black tracking-tight text-foreground">
                      {stat.value}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Right: 3D Floating QR Scene */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:flex items-center justify-center perspective-1200"
            >
              <div className="relative w-[420px] h-[420px]">
                {/* Main 3D QR Card */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-float-3d preserve-3d">
                    <div className="w-[280px] h-[280px] rounded-3xl bg-card border border-border/60 shadow-2xl shadow-primary/10 p-6 relative overflow-hidden">
                      {/* Card shimmer */}
                      <div className="absolute inset-0 shimmer-3d opacity-60" />
                      
                      {/* QR Code visual */}
                      <div className="relative w-full h-full rounded-2xl bg-white dark:bg-zinc-100 p-4 flex items-center justify-center">
                        {/* Stylized QR pattern */}
                        <svg viewBox="0 0 100 100" className="w-full h-full">
                          {/* Corner squares */}
                          <rect x="4" y="4" width="24" height="24" rx="4" fill="hsl(var(--primary))" opacity="0.9" />
                          <rect x="8" y="8" width="16" height="16" rx="2" fill="white" />
                          <rect x="11" y="11" width="10" height="10" rx="1.5" fill="hsl(var(--primary))" />
                          
                          <rect x="72" y="4" width="24" height="24" rx="4" fill="hsl(var(--primary))" opacity="0.9" />
                          <rect x="76" y="8" width="16" height="16" rx="2" fill="white" />
                          <rect x="79" y="11" width="10" height="10" rx="1.5" fill="hsl(var(--primary))" />
                          
                          <rect x="4" y="72" width="24" height="24" rx="4" fill="hsl(var(--primary))" opacity="0.9" />
                          <rect x="8" y="76" width="16" height="16" rx="2" fill="white" />
                          <rect x="11" y="79" width="10" height="10" rx="1.5" fill="hsl(var(--primary))" />
                          
                          {/* Data modules */}
                          {[
                            [34,6],[40,6],[46,6],[52,6],[58,6],[64,6],
                            [34,12],[46,12],[58,12],
                            [6,34],[12,34],[18,34],[24,34],[34,34],[40,34],[46,34],[52,34],[58,34],[64,34],[72,34],[78,34],[84,34],[90,34],
                            [6,40],[24,40],[40,40],[52,40],[64,40],[78,40],[90,40],
                            [6,46],[12,46],[18,46],[24,46],[34,46],[46,46],[58,46],[64,46],[72,46],[84,46],[90,46],
                            [6,52],[24,52],[34,52],[40,52],[46,52],[52,52],[58,52],[72,52],[78,52],[84,52],[90,52],
                            [6,58],[12,58],[18,58],[24,58],[40,58],[52,58],[64,58],[78,58],[90,58],
                            [6,64],[24,64],[34,64],[46,64],[58,64],[64,64],[72,64],[84,64],[90,64],
                            [34,72],[40,72],[52,72],[64,72],[72,72],[78,72],[84,72],[90,72],
                            [34,78],[46,78],[58,78],[72,78],[90,78],
                            [34,84],[40,84],[46,84],[52,84],[58,84],[64,84],[72,84],[78,84],[84,84],[90,84],
                            [34,90],[52,90],[64,90],[78,90],[90,90],
                          ].map(([x, y], i) => (
                            <rect key={i} x={x} y={y} width="4.5" height="4.5" rx="1" fill="hsl(var(--primary))" opacity={0.6 + (i % 5) * 0.08} />
                          ))}
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Orbiting elements */}
                {/* Analytics badge - top right */}
                <motion.div
                  className="absolute -top-2 -right-2"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="animate-float-3d-reverse preserve-3d">
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border/60 shadow-lg shadow-black/5">
                      <div className="w-8 h-8 rounded-lg gradient-hero flex items-center justify-center">
                        <Activity className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground font-medium">Scans today</div>
                        <div className="text-sm font-black text-foreground">2,847</div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Scan indicator - bottom left */}
                <motion.div
                  className="absolute -bottom-4 -left-4"
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                >
                  <div className="animate-float-3d preserve-3d">
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-card border border-border/60 shadow-lg shadow-black/5">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <Scan className="h-4 w-4 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground font-medium">Dynamic</div>
                        <div className="text-sm font-black text-emerald-600 dark:text-emerald-400">Active</div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Views badge - middle left */}
                <motion.div
                  className="absolute top-1/2 -left-12 -translate-y-1/2"
                  animate={{ x: [0, -6, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                >
                  <div className="animate-float-3d-reverse preserve-3d">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border/60 shadow-lg shadow-black/5">
                      <Eye className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-bold">12.4K</span>
                    </div>
                  </div>
                </motion.div>

                {/* Floating dots */}
                <motion.div
                  className="absolute top-8 left-8 w-3 h-3 rounded-full bg-primary/30"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                  className="absolute bottom-16 right-12 w-2 h-2 rounded-full bg-accent/40"
                  animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0.7, 0.4] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
                />

                {/* Ring decoration */}
                <motion.div
                  className="absolute inset-[-30px] rounded-full border border-primary/8"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                />
                <motion.div
                  className="absolute inset-[-60px] rounded-full border border-dashed border-border/20"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
              Everything you need,
              <br />
              <span className="text-muted-foreground">nothing you don't</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Professional QR codes at scale — from creation to analytics.
            </p>
          </motion.div>

          {/* 3D Feature grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => (
              <Feature3DCard key={feature.title} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero opacity-95" />
        <div className="absolute inset-0 dot-grid opacity-10" />
        
        {/* 3D Geometric accents */}
        <motion.div 
          className="absolute top-8 left-8 w-32 h-32 rounded-full border border-white/10"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div 
          className="absolute bottom-8 right-8 w-48 h-48 rounded-full border border-white/10"
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute top-1/3 right-[15%] w-20 h-20 rounded-2xl border border-white/8 rotate-45"
          animate={{ y: [0, -20, 0], rotate: [45, 55, 45] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 left-[10%] w-16 h-16 rounded-xl border border-white/6"
          animate={{ y: [0, 15, 0], rotate: [0, -10, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <div className="absolute top-1/2 left-1/4 w-2 h-2 rounded-full bg-white/20" />
        <div className="absolute top-1/4 right-1/3 w-1.5 h-1.5 rounded-full bg-white/15" />

        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight mb-6 leading-[1.05]">
              Ready to create QR codes
              <br />
              that perform?
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-lg mx-auto">
              Join 50,000+ businesses and creators using NexQR to engage their audience.
            </p>
            <Link to="/create">
              <Button size="lg" className="group bg-white text-foreground hover:bg-white/95 h-12 px-8 text-[15px] font-bold shadow-xl rounded-xl">
                <QrCode className="h-4 w-4 mr-2" />
                Create Your First QR Code
                <ArrowUpRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
            </Link>
            
            <p className="mt-6 text-white/50 text-sm">
              No credit card required · Free forever plan available
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
